import type { TFunction } from 'i18next';

/**
 * EAA-1883: `UserSyncService`/`OrganizationSyncService` (portal-api) started
 * as raw English prose per error row (e.g. "record carries no scope_id —
 * cannot resolve an organization for this user"), rendered verbatim inside an
 * otherwise fully-Vietnamese console. A sibling BE change (same ticket) adds a
 * structured `error_code` alongside the existing human-readable `error`
 * string (kept for technical/debug purposes) — this file maps the known codes
 * to a professional Vietnamese/English sentence via i18n, and falls back to
 * the raw string — explicitly labeled as a technical error, never hidden —
 * for any code this map does not (yet) recognize.
 *
 * EAA-1883 update: the sibling BE commit landed (`UserSyncService`'s own
 * docstring in `portal_api/directory/services.py` enumerates the codes below;
 * `OrganizationSyncService` produces a further set —
 * `invalid_organization_row`/`invalid_schools_list`/`invalid_school_row`/
 * `missing_scope_id` — deliberately left unmapped here since they fall back
 * to the raw-string "technical error" path just fine and org-sync errors are
 * genuinely rare/malformed-input cases, not ones worth a bespoke sentence).
 * `no_scope_id` moved OUT of `UserSyncService`'s `errors: []` into a sibling
 * `skipped: []` list (terminal, non-actionable — see `UserSyncSkipped` in
 * `@/api/console`) but its `error_code` value and this map entry are
 * unchanged, since `SyncUsersModal` reuses `describeSyncError` for both lists.
 */
export type SyncErrorCode =
    | 'no_scope_id'
    | 'unresolved_scope'
    | 'keycloak_rejected'
    | 'missing_identity_fields';

/** Maps a backend `error_code` to its `console.apps.syncErrors.*` i18n key. */
const SYNC_ERROR_CODE_KEYS: Record<SyncErrorCode, string> = {
    no_scope_id: 'noScopeId',
    unresolved_scope: 'unresolvedScope',
    keycloak_rejected: 'keycloakRejected',
    missing_identity_fields: 'missingIdentityFields',
};

/** Shape common to `UserSyncError` and `OrganizationSyncError` once the sibling
 *  BE change lands — both keep their existing `error` string and gain an
 *  optional `error_code` (+ `scope_id` for `unresolved_scope`). Loosely typed
 *  here (rather than importing the API interfaces) so this helper works
 *  against either row shape without a dependency on `@/api/console`. */
export interface SyncErrorLike {
    error?: string;
    message?: string;
    error_code?: string | null;
    scope_id?: string;
}

export interface SyncErrorDisplay {
    /** `true` when `error_code` matched a known case and `text` is the
     *  friendly translation; `false` when it fell back to the raw string. */
    isKnown: boolean;
    text: string;
}

function isKnownSyncErrorCode(code: string): code is SyncErrorCode {
    return Object.prototype.hasOwnProperty.call(SYNC_ERROR_CODE_KEYS, code);
}

/**
 * EAA-1883: translate one sync error row for display. A recognized
 * `error_code` renders a professional, translated sentence (with `scope_id`
 * interpolated where the message needs it); an absent or unrecognized code
 * falls back to the raw `error`/`message` string so nothing is ever silently
 * dropped — callers should label that fallback distinctly (e.g. a "technical
 * error" badge) rather than presenting it as if it were the friendly case.
 */
export function describeSyncError(row: SyncErrorLike, t: TFunction): SyncErrorDisplay {
    const code = row.error_code;
    const rawMessage = row.error ?? row.message ?? '';

    if (code && isKnownSyncErrorCode(code)) {
        const key = SYNC_ERROR_CODE_KEYS[code];
        return { isKnown: true, text: t(`console.apps.syncErrors.${key}`, { scopeId: row.scope_id }) };
    }

    return { isKnown: false, text: rawMessage };
}
